import {Fragment, useContext, useState} from 'react';
import {Title} from '@app/titles/models/title';
import {TitlePageHeaderLayout} from '@app/titles/pages/title-page/title-page-header-layout';
import {FormattedDate} from '@common/i18n/formatted-date';
import {FormattedDuration} from '@common/i18n/formatted-duration';
import {BulletSeparatedItems} from '@app/titles/bullet-separated-items';
import {TitlePoster} from '@app/titles/title-poster/title-poster';
import {WatchlistButton} from '@app/user-lists/watchlist-button';
import {getInputFieldClassNames} from '@common/ui/forms/input-field/get-input-field-class-names';
import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body';
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header';
import {DialogFooter} from '@common/ui/overlays/dialog/dialog-footer';
import {Button} from '@common/ui/buttons/button';
import {DialogTrigger} from '@common/ui/overlays/dialog/dialog-trigger';
import {Option, Select} from '@common/ui/forms/select/select';
import clsx from 'clsx';
import {Trans} from '@common/i18n/trans';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import {DialogContext} from '@common/ui/overlays/dialog/dialog-context';
import reportLight from './reportLightMode.png';
import reportDark from './reportDarkMode.png';
import {useAuth} from '@common/auth/use-auth';
import imdbLogo from '../../../../../public/assets/images/movies/imdb-logo.svg';
import imdbLogoDisabled from '../../../../../public/assets/images/movies/imdb-b.png';
import {GoClockFill} from 'react-icons/go';
import {FaCalendarAlt} from 'react-icons/fa';
import {Global} from 'iconsax-react';
import {useReportTitle} from '@app/admin/titles/requests/use-report-title';
import {toast} from '@common/ui/toast/toast';
import {useLocation} from 'react-router-dom';
import Plus3 from '../../../../../public/assets/images/age/iCons_3_Plus.png';
import Plus7 from '../../../../../public/assets/images/age/iCons_7_Plus.png';
import Plus9 from '../../../../../public/assets/images/age/iCons_9_Plus.png';
import Plus10 from '../../../../../public/assets/images/age/iCons_10_Plus.png';
import Plus13 from '../../../../../public/assets/images/age/iCons_13_Plus.png';
import Plus14 from '../../../../../public/assets/images/age/iCons_14_Plus.png';
import Plus15 from '../../../../../public/assets/images/age/iCons_15_Plus.png';
import Plus16 from '../../../../../public/assets/images/age/iCons_16_Plus.png';
import Plus17 from '../../../../../public/assets/images/age/iCons_17_Plus.png';
import Plus18 from '../../../../../public/assets/images/age/iCons_18_Plus.png';
import Approved from '../../../../../public/assets/images/age/iCons_Approved.png';
import Nr from '../../../../../public/assets/images/age/iCons_NR.png';
import {useTrans} from '@common/i18n/use-trans';
import {useAddToWatchlist} from '@app/user-lists/requests/use-add-to-watchlist';
import {useIsItemWatchlisted} from '@app/user-lists/requests/use-current-user-watchlist';
import {useSelectedLocale} from '@common/i18n/selected-locale';

interface Props {
  className?: string;
  title: Title;
  showPoster?: boolean;
}
export function TitlePageHeader({className, title, showPoster = false}: Props) {
  const location = useLocation();
  const pathname = location.pathname;

  const {locale} = useSelectedLocale();

  const {selectedTheme} = useThemeSelector();
  const {isLoggedIn} = useAuth();

  const {trans} = useTrans();

  const restrictionSvgPlacer = (certification?: string) => {
    const makePureCertification = certification
      ?.replaceAll(',', '')
      .toLowerCase();
    let svg: string = '';
    switch (makePureCertification) {
      case 'tv-y':
      case 'tv-g':
      case 'y7':
      case 'tv-y7':
      case 'tv-y7-f7':
      case 'g':
        svg = Plus3;
        break;
      case '7':
      case '+7':
      case '7+':
        svg = Plus7;
        break;
      case 'pg':
        svg = Plus10;
        break;
      case 'pg-13':
      case '+13':
        svg = Plus13;
        break;
      case 'tv-14':
      case '14':
        svg = Plus14;
        break;
      case 'ma':
        svg = Plus15;
        break;
      case '+16':
        svg = Plus16;
        break;
      case 'nc-17':
      case 'tv-ma':
      case 'r':
      case '+18':
      case '18+':
        svg = Plus18;
        break;
      case 'nr':
      case 'not rated':
      case 'notrated':
      case 'unrated':
      case 'gp':
        svg = Nr;
        break;
      case 'approved':
      case 'passed':
        svg = Approved;
        break;
      default:
        svg = Nr;
        break;
    }
    return svg;
  };

  const placeTitle = (certification?: string) => {
    const makePureCertification = certification
      ?.replaceAll(',', '')
      .toLowerCase();
    let titleString: string = '';
    switch (makePureCertification) {
      case 'tv-y':
      case 'tv-g':
      case 'y7':
      case 'tv-y7':
      case 'tv-y7-f7':
        titleString = 'This Title is suitable for people above age 3';
        break;
      case '7':
      case '+7':
      case '7+':
        titleString = 'This Title is suitable for people above age 7';
        break;
      case 'pg':
        titleString = 'This Title is suitable for people above age 10';
        break;
      case 'pg-13':
      case '+13':
        titleString = 'This Title is suitable for people above age 13';
        break;
      case 'tv-14':
      case '14':
        titleString = 'This Title is suitable for people above age 14';
        break;
      case 'ma':
        titleString = 'This Title is suitable for people above age 15';
        break;
      case '+16':
        titleString = 'This Title is suitable for people above age 16';
        break;
      case 'nc-17':
      case 'tv-ma':
      case 'r':
      case '+18':
      case '18+':
        titleString = 'This Title is suitable for people above age 18';
        break;
      case 'nr':
      case 'not rated':
      case 'notrated':
      case 'unrated':
      case 'g':
      case 'gp':
        titleString =
          'This Title is not rated and does not have age restriction';
        break;
      case 'approved':
      case 'passed':
        titleString =
          'This Title is related to years before 1968 where there was only approved and not approved restrictions';
        break;
    }
    return trans({
      message: titleString,
    });
  };

  const goBackOneSlash = () => {
    const path = window.location.pathname;
    const newPath = path.substring(0, path.lastIndexOf('/')) || '/';
    return newPath;
  };

  return (
    <TitlePageHeaderLayout
      className={className}
      // name={<TitleLink title={title} />}
      // name={<Trans message={title.name} />}
      name={
        locale.language === 'fa' && title?.persian_name
          ? title?.persian_name
          : title?.name
      }
      poster={
        showPoster ? (
          <TitlePoster
            title={title}
            size="w-80"
            srcSize="sm"
            link={goBackOneSlash()}
          />
        ) : null
      }
      description={
        <div>
          <BulletSeparatedItems isInTitle>
            <div className="flex items-end gap-x-4 text-base max-md:text-xs">
              <FaCalendarAlt
                title={trans({message: 'Title Release Date'})}
                fontSize={26}
                color="#fff"
              />
              <FormattedDate date={title.release_date} />
            </div>
            {title.runtime && (
              <div className="flex items-center gap-x-4">
                <GoClockFill
                  title={trans({message: 'Title Runtime'})}
                  fontSize={30}
                  color="#fff"
                />
                <FormattedDuration minutes={title.runtime} verbose />
              </div>
            )}
            {!pathname.includes('full-credits') &&
            title?.production_countries?.length !== 0 ? (
              <div className="flex items-end gap-x-4 text-base max-md:text-xs">
                <Global size="30" color="#fff" variant="Bold" />
                {title?.production_countries?.map((country, index) => (
                  <Fragment key={country.id}>
                    <Trans message={country?.display_name} />
                    {index === title?.production_countries?.length - 1
                      ? null
                      : ' , '}
                  </Fragment>
                ))}
              </div>
            ) : null}
            {title.certification && (
              <img
                className="h-32 w-32"
                src={
                  title.certification
                    ? restrictionSvgPlacer(title.certification)
                    : Nr
                }
                alt=""
                title={placeTitle(title.certification)}
              />
              // <div className="uppercase">{title.certification}</div>
            )}
          </BulletSeparatedItems>
        </div>
      }
      right={
        <div className="flex items-center gap-10 px-10 max-md:!ml-auto max-md:mt-10 max-md:!px-0 md:flex-row">
          {/* <InteractableRating title={title} /> */}
          <span className="rateText text-base font-semibold max-md:text-xs">
            {/* {title.tmdb_vote_average} / 10 */}
            {title.rating === 0 || !title.rating
              ? null
              : `${title.rating} / 10`}
            {/* {title.rating} / 10 */}
          </span>
          <img
            className="cursor-pointer"
            src={
              title.rating === 0 || !title.rating ? imdbLogoDisabled : imdbLogo
            }
            alt=""
            onClick={() => {
              if (!title.imdb_id) return;
              window.open(`https://www.imdb.com/title/${title.imdb_id}`);
            }}
          />

          {/* <ReportButton video={title.videos[0]} /> */}
          {isLoggedIn && (
            <Fragment>
              <DialogTrigger type="modal">
                <Button
                  startIcon={
                    <img
                      src={selectedTheme.is_dark ? reportDark : reportLight}
                      alt=""
                      className="h-24 w-24"
                    />
                  }
                  className="gap-1"
                >
                  {' '}
                  <span className="text-sm font-semibold">
                    <Trans message="Report" />
                  </span>
                </Button>
                <SendRequest title={title} />
              </DialogTrigger>
              <WatchlistButton item={title} size="sm" />
            </Fragment>
          )}
        </div>
      }
    />
  );
}

export const SendRequest = ({title}: {title: Title}) => {
  const inputFieldClassNames = getInputFieldClassNames();
  const {close} = useContext(DialogContext);
  const {isWatchlisted} = useIsItemWatchlisted(title);

  const addToWatchlist = useAddToWatchlist();

  const {selectedTheme} = useThemeSelector();

  const subjects = {
    activate: 'درخواست فعال سازی ویدیو',
    online_play: 'مشکل پخش آنلاین',
    download_link: 'مشکل لینک دانلود',
    descriptions: 'مشکل در توضیحات',
    subtract: 'مشکل در زیرنویس',
    certificate: 'این محتوا برای کودکان مناسب نیست',
    others: 'غیره...',
  };

  const [request, setRequest] = useState<{subject: string; desc: string}>({
    subject: subjects.activate,
    desc: '',
  });

  const reportTitle = useReportTitle(title.id.toString(), {
    subject: request.subject,
    reason: request.desc,
  });
  return (
    <Dialog>
      <DialogHeader>
        <Trans message="Send Request" />
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-20">
          <div className="flex w-full flex-col items-start">
            <label htmlFor="" className={inputFieldClassNames.label}>
              <Trans message="subject" />
            </label>
            <Select
              minWidth="min-w-none"
              className="mb-24 w-full bg"
              selectionMode="single"
              selectedValue={request.subject}
              onSelectionChange={(s: any) =>
                setRequest(prev => ({
                  ...prev,
                  subject: s as string,
                }))
              }
            >
              {Object.keys(subjects).map((subject, i) => (
                <Option
                  key={i}
                  value={subjects[subject as keyof typeof subjects]}
                >
                  {subjects[subject as keyof typeof subjects]}
                </Option>
              ))}
            </Select>
          </div>{' '}
          <div className="flex w-full flex-col items-start">
            <label htmlFor="" className={inputFieldClassNames.label}>
              <Trans message="description" />
            </label>
            <textarea
              placeholder="بیشتر توضیح دهید..."
              value={request.desc}
              onChange={e =>
                setRequest(prev => ({
                  ...prev,
                  desc: e.target.value,
                }))
              }
              className={clsx(
                inputFieldClassNames.input,
                'h-80 w-full resize-none text-right',
              )}
            />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button onClick={() => close()}>
          <Trans message="Cancel" />
        </Button>
        <Button
          variant="flat"
          color="primary"
          type="submit"
          onClick={() => {
            if (!request.desc) return toast.danger('Please enter description');
            close();
            if (
              request.subject === subjects.activate ||
              request.subject === subjects.download_link ||
              request.subject === subjects.online_play
            ) {
              if (!isWatchlisted) {
                addToWatchlist.mutate(title);
              }
            }
            reportTitle.mutate();
          }}
        >
          <Trans message="Send" />
        </Button>{' '}
      </DialogFooter>
    </Dialog>
  );
};
